import CONSTANTS from 'src/utils/constants';
import { AppModules } from 'src/models/permissions/RolePermissions';

/**
 * Per-CLI source chips for the LEF report (Epic 3, Stories 3.3 + 3.4). No React.
 *
 * Where the data comes from: one batch call for many CLIs, POST /cli-run-sources
 * (BatchGetCliRunSources) → per CLI: status, the latest real run in the period, the calculation version
 * that run used and that version's Step 2 data sources, reduced to type, description, CDT / TPA / MDA id
 * and the custom COA tables used (`customCoaTables`), with no names for CDT / TPA / MDA. Because the
 * sources are those of the run's own version, the chips describe the configuration that produced the
 * report's numbers, not whatever is configured today (3.3 AC3).
 *
 * Nothing is inferred: a CLI with no run for the period, or no data sources, gets no chips and an
 * explicit "unavailable" state instead.
 */

export type CliSourceType = 'GL' | 'MDA' | 'TPA' | 'CDT' | 'COA';

export interface CliSourceChip {
    type: CliSourceType;
    /** "GL Balance", "MDA", "CDT1", ... */
    label: string;
    /** Tooltip: the data source names / table names this chip stands for. */
    title: string;
    /** In-app route of the origin (3.4 AC1); null when there is no page to open. */
    href: string | null;
    /**
     * GL balances have no page of their own; their origin is the CLI's Calc Builder Step 2. That link
     * needs the CLI recordId (a separate lookup), so the GL chip is flagged and the header opens it
     * through the same on-click path as the CLI number (Story 3.5) instead of a precomputed href.
     */
    opensCalcBuilder?: boolean;
    /** Module gating the target page, for the permission check (3.4 AC5). */
    module: string;
    /** True when the target page opens the version used at calc time (3.4 AC3). */
    versionPinned: boolean;
}

export const SOURCE_TYPE_LABEL: Record<CliSourceType, string> = {
    GL: 'GL Balance',
    MDA: 'MDA',
    TPA: 'TPA',
    CDT: 'CDT',
    COA: 'COA',
};

/** Chip order in a header: base (GL) first, then drivers, then classifications. */
const TYPE_ORDER: CliSourceType[] = ['GL', 'MDA', 'TPA', 'CDT', 'COA'];

// ---------------------------------------------------------------------------------------------------
// Period
// ---------------------------------------------------------------------------------------------------

/**
 * LEF periods are "MAY-26"; the calculation-executions endpoint takes "MAY-2026" (the format Calc
 * Builder's execution-period menu sends). Returns null for anything it cannot convert.
 */
export function toExecutionPeriod(lefPeriod: string | null | undefined): string | null {
    const match = /^([A-Za-z]{3})-(\d{2}|\d{4})$/.exec((lefPeriod ?? '').trim());
    if (!match) {
        return null;
    }
    const year = match[2].length === 2 ? `20${match[2]}` : match[2];
    return `${match[1].toUpperCase()}-${year}`;
}

// ---------------------------------------------------------------------------------------------------
// Chip derivation
// ---------------------------------------------------------------------------------------------------

interface SourceEntry {
    type: CliSourceType;
    /** Identity for de-duplication: one chip per distinct underlying object. */
    key: string;
    name: string;
    href: string | null;
    module: string;
    versionPinned: boolean;
}

const nonEmpty = (value: unknown): string | undefined => {
    const text = value === undefined || value === null ? '' : `${value}`.trim();
    return text ? text : undefined;
};

/** The custom COA tables a data source's keys use, as the batch endpoint sends them (`customCoaTables`). */
function customCoaTables(dataSource: any): { tableId: string; tableName?: string }[] {
    const tables: { tableId: string; tableName?: string }[] = [];
    (Array.isArray(dataSource?.customCoaTables) ? dataSource.customCoaTables : []).forEach((table: any) => {
        const tableId = nonEmpty(table?.tableId);
        if (tableId) {
            tables.push({ tableId, tableName: nonEmpty(table.tableName) });
        }
    });
    return tables;
}

/** Maps one raw data source (API shape) to the chip entries it contributes. */
export function sourceEntriesOf(dataSource: any): SourceEntry[] {
    const entries: SourceEntry[] = [];
    const type = dataSource?.datasourceType;
    const description = nonEmpty(dataSource?.description) ?? '';

    switch (type) {
        case CONSTANTS.DATA_SOURCE_TYPES.GENERAL_LEDGER:
        case CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER:
            // All GL balances collapse into one "GL Balance" chip; there is no dedicated GL page, so the
            // chip links to the CLI's Calc Builder Step 2 (added by the caller, which knows the recordId).
            entries.push({ type: 'GL', key: 'GL', name: description, href: null, module: AppModules.CALCULATION_BUILDER, versionPinned: false });
            customCoaTables(dataSource).forEach(({ tableId, tableName }) =>
                entries.push({
                    type: 'COA',
                    key: `COA:${tableId}`,
                    name: tableName ?? tableId,
                    href: `${CONSTANTS.PAGE_NAV.CUSTOM_COA_DETAILS.URL}/${btoa(tableId)}`,
                    module: AppModules.CUSTOM_COA,
                    versionPinned: false,
                })
            );
            break;
        case CONSTANTS.DATA_SOURCE_TYPES.CUSTOM_DATA_TABLE: {
            const tableId = nonEmpty(dataSource?.customDatasourceTableId);
            entries.push({
                type: 'CDT',
                key: `CDT:${tableId ?? description}`,
                name: description,
                href: tableId ? `${CONSTANTS.PAGE_NAV.CUSTOM_DATA_TABLE_DETAILS.URL}/${btoa(tableId)}` : null,
                module: AppModules.CUSTOM_DATA_TABLES,
                versionPinned: false,
            });
            break;
        }
        case CONSTANTS.DATA_SOURCE_TYPES.TP_ALLOCATION_WORKSHEET: {
            const worksheetId = nonEmpty(dataSource?.tpAllocationWorksheetId);
            entries.push({
                type: 'TPA',
                key: `TPA:${worksheetId ?? description}`,
                name: description,
                // Same target the existing passthrough drill-down links use.
                href: worksheetId ? `${CONSTANTS.PAGE_NAV.TP_ALLOCATION_STANDALONE.URL}/${btoa(worksheetId)}` : null,
                module: AppModules.TP_ALLOCATION,
                versionPinned: false,
            });
            break;
        }
        case CONSTANTS.DATA_SOURCE_TYPES.MULTIDIMENSIONAL_ALLOCATION_WORKSHEET: {
            const worksheetId = nonEmpty(dataSource?.multiAllocationWorksheetId);
            // Keyed by worksheet: several portions of one worksheet (R&D / Non-R&D rows) are one
            // source reference and one chip (3.3 AC6).
            entries.push({
                type: 'MDA',
                key: `MDA:${worksheetId ?? description}`,
                name: description,
                href: worksheetId
                    ? `${CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_CALCULATION_BUILDER.URL}/${worksheetId}`
                    : null,
                module: AppModules.MULTIDIMENSIONAL_ALLOCATION,
                versionPinned: false,
            });
            break;
        }
        default:
            // Unknown types are not guessed at.
            break;
    }
    return entries;
}

/**
 * Chips for one CLI (3.3): one per distinct source, numbered when a type repeats ("CDT1", "CDT2"),
 * ordered GL → MDA → TPA → CDT → COA.
 */
export function buildSourceChips(dataSources: ReadonlyArray<any>): CliSourceChip[] {
    const byKey = new Map<string, SourceEntry & { names: string[] }>();
    dataSources.forEach((dataSource) =>
        sourceEntriesOf(dataSource).forEach((entry) => {
            const existing = byKey.get(entry.key);
            if (existing) {
                if (entry.name && !existing.names.includes(entry.name)) {
                    existing.names.push(entry.name);
                }
            } else {
                byKey.set(entry.key, { ...entry, names: entry.name ? [entry.name] : [] });
            }
        })
    );

    const chips: CliSourceChip[] = [];
    TYPE_ORDER.forEach((type) => {
        const ofType = Array.from(byKey.values()).filter((entry) => entry.type === type);
        ofType.forEach((entry, index) => {
            const label = ofType.length > 1 ? `${SOURCE_TYPE_LABEL[type]}${index + 1}` : SOURCE_TYPE_LABEL[type];
            chips.push({
                type,
                label,
                title: entry.names.length > 0 ? entry.names.join(', ') : SOURCE_TYPE_LABEL[type],
                href: entry.href,
                opensCalcBuilder: type === 'GL' || undefined,
                module: entry.module,
                versionPinned: entry.versionPinned,
            });
        });
    });
    return chips;
}

// ---------------------------------------------------------------------------------------------------
// Batch lookup
// ---------------------------------------------------------------------------------------------------

export const SOURCES_UNAVAILABLE_NO_RUN = 'No calculation run was found for this period, so its sources are unavailable.';
export const SOURCES_UNAVAILABLE_NONE = 'No data sources are recorded for this calculation run.';

export type CliSourcesResult =
    | { status: 'loaded'; chips: CliSourceChip[] }
    | { status: 'unavailable'; reason: string };

export interface CliSourcesFetchers {
    /** Batch lookup (POST /cli-run-sources): one entry per requested CLI. */
    fetchRunSourcesBatch: (clis: string[], period: string) => Promise<CliRunSourcesEntry[]>;
}

/** One CLI's entry in a BatchGetCliRunSources response. */
export interface CliRunSourcesEntry {
    calculationNumber: string;
    /** OK | NO_RUN | NO_VERSION | ERROR */
    status: string;
    message?: string;
    calculationVersion?: number;
    dryRun?: boolean;
    dataSources?: any[];
}

export const SOURCES_BATCH_MISSING_RESULT = 'No sources were returned for this CLI.';
export const SOURCES_BATCH_FAILED = 'Could not load the data sources for this CLI.';

/**
 * Maps one batch entry to the CLI's chip state. `error` results are not to be cached (they retry on the
 * next reveal); `loaded` and `unavailable` are final for the session.
 */
export function resultFromRunSources(
    entry: CliRunSourcesEntry | undefined
): CliSourcesResult | { status: 'error'; message: string } {
    switch (entry?.status) {
        case 'OK': {
            const chips = buildSourceChips(entry.dataSources ?? []);
            return chips.length > 0 ? { status: 'loaded', chips } : { status: 'unavailable', reason: SOURCES_UNAVAILABLE_NONE };
        }
        case 'NO_RUN':
        case 'NO_VERSION':
            return { status: 'unavailable', reason: SOURCES_UNAVAILABLE_NO_RUN };
        case 'ERROR':
            return { status: 'error', message: nonEmpty(entry.message) ?? SOURCES_BATCH_FAILED };
        case undefined:
            return { status: 'error', message: SOURCES_BATCH_MISSING_RESULT };
        default:
            return { status: 'error', message: SOURCES_BATCH_FAILED };
    }
}
